import { memo, useCallback, useEffect, useRef, type CSSProperties, type ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'
import {
  MentionableBackendPlugin,
  MentionableBrowserTab,
  MentionableItem,
  MentionableModel,
  MentionableShortcut,
} from '../../../../../types'
import { Badge } from '../../../../elements/Badge'
import { ProBadge } from '../../../../elements/ProBadge'
import { ShortcutIcon } from '../../../../shortcuts/ShortcutIcon'
import { CONTEXT_PLUGINS } from '../backend-plugins'
import { useDefaultModelId } from '../hooks/use-default-model-id'
import { HighlightLogo, listItemVariants, Tooltip } from '@highlight/ui'
import { mentionTourTarget } from '@renderer/overlay/Sidepanel/components/day-zero-tour/missions'

type MentionableRowProps = {
  mention: MentionableItem
  isSelected: boolean
  onSelect: (mention: MentionableItem) => void
}

/**
 * Model and plugin icons arrive as raw SVG markup rather than a URL. Used as a mask over the row's
 * text colour, so they follow the theme and the row's state like a Lucide icon does.
 */
const svgBackground = (svg: string): CSSProperties => {
  const url = `url('data:image/svg+xml;utf8,${encodeURIComponent(svg)}')`
  return {
    backgroundColor: 'currentColor',
    maskImage: url,
    WebkitMaskImage: url,
    maskSize: 'contain',
    WebkitMaskSize: 'contain',
    maskRepeat: 'no-repeat',
    WebkitMaskRepeat: 'no-repeat',
    maskPosition: 'center',
    WebkitMaskPosition: 'center',
  }
}

const iconClassName = 'text-foreground flex h-4 w-4 items-center justify-center'

interface MentionPresentation {
  icon: ReactNode
  badges: ReactNode
}

interface MentionPresentationContext {
  defaultModelId: string | undefined
}

/** Each mention type's icon + trailing badges, self-contained per type. */
const MENTION_PRESENTERS: {
  shortcut: (mention: MentionableShortcut) => MentionPresentation
  browser_tab: (mention: MentionableBrowserTab) => MentionPresentation
  backend_plugin: (mention: MentionableBackendPlugin) => MentionPresentation
  model: (mention: MentionableModel, ctx: MentionPresentationContext) => MentionPresentation
} = {
  shortcut: (mention) => ({
    icon: mention.images ? (
      <ShortcutIcon
        image={mention.images.image ?? undefined}
        extension={mention.images.userImages?.file_extension ?? undefined}
        size={16}
      />
    ) : null,
    badges: null,
  }),
  browser_tab: (mention) => ({
    icon: mention.images?.image ? <img src={mention.images.image} alt="" className={'h-4 w-4 rounded-lg'} /> : null,
    badges: null,
  }),
  backend_plugin: (mention) => ({
    icon: mention.images?.image ? (
      mention.images.image.startsWith('<svg') ? (
        <div className={iconClassName} style={svgBackground(mention.images.image)} />
      ) : (
        <img src={mention.images.image} alt="" className={'h-4 w-4 rounded-lg'} />
      )
    ) : null,
    badges:
      mention.isPro === true ? (
        <ProBadge size="sm" />
      ) : mention.isPro === false ? (
        <Tooltip>
          <Tooltip.Trigger render={<div className="relative flex" />}>
            <HighlightLogo size={12} tone="light" aria-hidden>
              <HighlightLogo.Icon />
            </HighlightLogo>
          </Tooltip.Trigger>
          <Tooltip.Content>
            {CONTEXT_PLUGINS.includes(mention.value) ? 'Context Search Tool' : 'Made by Highlight'}
          </Tooltip.Content>
        </Tooltip>
      ) : null,
  }),
  model: (mention, { defaultModelId }) => ({
    icon: mention.images?.image ? (
      <div className={iconClassName} style={svgBackground(mention.images.image)} />
    ) : (
      <div className="bg-foreground h-5 w-5 rounded-full" />
    ),
    badges: (
      <>
        {mention.pricing?.isFree === false && (
          <div className="flex items-center gap-2">
            <ProBadge size="sm" />
            {mention.pricing?.priceTier && (
              <span className="text-foreground-disabled text-xs font-medium">
                {'$'.repeat(mention.pricing.priceTier)}
              </span>
            )}
          </div>
        )}
        {mention.meta?.experimental === true && <Badge variant={'beta'}>BETA</Badge>}
        {mention.id === defaultModelId && <Badge variant={'modelDefault'}>Default</Badge>}
        {mention.capabilities?.supportsVision === false && (
          <p className={'ml-2 truncate text-xs opacity-30'}>Images not supported</p>
        )}
      </>
    ),
  }),
}

const getMentionPresentation = (mention: MentionableItem, ctx: MentionPresentationContext): MentionPresentation => {
  switch (mention.type) {
    case 'shortcut':
      return MENTION_PRESENTERS.shortcut(mention)
    case 'browser_tab':
      return MENTION_PRESENTERS.browser_tab(mention)
    case 'backend_plugin':
      return MENTION_PRESENTERS.backend_plugin(mention)
    case 'model':
      return MENTION_PRESENTERS.model(mention, ctx)
  }
}

/** What a mention row shows: its icon, label and badges. Shared by the inline list and the Combobox. */
export const MentionRowContent = ({ mention }: { mention: MentionableItem }) => {
  const defaultModelId = useDefaultModelId()
  const { icon, badges } = getMentionPresentation(mention, { defaultModelId })
  return (
    <span className="flex min-w-0 flex-1 items-center gap-2">
      {icon}
      <span className="min-w-0 truncate">{mention.label}</span>
      {badges}
    </span>
  )
}

export const MentionableRow = memo(({ mention, isSelected, onSelect }: MentionableRowProps) => {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!ref.current || !isSelected) {
      return
    }
    ref.current.scrollIntoView({ block: 'end', inline: 'nearest', behavior: 'smooth' })
  }, [isSelected])

  const handleClick = useCallback(() => onSelect(mention), [onSelect, mention])

  return (
    <div
      ref={ref}
      data-slot="list-item"
      className={twMerge(listItemVariants(), 'hover:bg-element-hover cursor-pointer')}
      data-highlighted={isSelected || undefined}
      onClick={handleClick}
      data-mention-type={mention.type}
      data-mention-value={mention.value}
      data-mention-label={mention.label}
      data-tour-section={mention.type === 'shortcut' ? mentionTourTarget(mention.label) : undefined}
    >
      <MentionRowContent mention={mention} />
    </div>
  )
})
MentionableRow.displayName = 'MentionableRow'
